import { computed, type Ref } from 'vue'
import { type PDataTableColumn, type PDataTableColumnName } from '../types'
import { type PersistedColumn, PersistenceKeys } from './types'
import { usePersistenceItem } from './index'

const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

const widthsEqual = (a: Record<string, number>, b: Record<string, number>): boolean => {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false
  }
  return true
}

export const usePersistedColumns = (
  namespace: string,
  columnsDefinition: Ref<readonly PDataTableColumn[]> | PDataTableColumn[],
  variables: {
    visibleColumns: Ref<PDataTableColumnName[] | undefined>
    columnOrder?: Ref<PDataTableColumnName[]>
    columnWidths?: Ref<Record<string, number>>
  },
): void => {
  const columnNames = computed<PDataTableColumnName[]>(() => {
    const defs = Array.isArray(columnsDefinition) ? columnsDefinition : columnsDefinition.value
    return defs.map((column) => column.name)
  })

  const applyColumnOrder = (names: string[]): string[] => {
    const known = columnNames.value
    const kept = names.filter((name) => known.includes(name))
    const added = known.filter((name) => !kept.includes(name))
    return [...kept, ...added]
  }

  const persistedColumns = computed<PersistedColumn[]>({
    get: () =>
      applyColumnOrder(
        variables.columnOrder?.value?.length ? variables.columnOrder.value : columnNames.value,
      ).map((name) => {
        const width = variables.columnWidths?.value?.[name]
        return {
          name,
          visible: variables.visibleColumns.value?.includes(name) ?? true,
          ...(typeof width === 'number' ? { width } : {}),
        }
      }),
    set: (value) => {
      const orderedNames = applyColumnOrder(value.map((col) => col.name))
      const visibleNames = value.filter((col) => col.visible).map((col) => col.name)
      const orderedVisible = orderedNames.filter((name) => visibleNames.includes(name))

      if (variables.columnOrder && !arraysEqual(variables.columnOrder.value ?? [], orderedNames)) {
        variables.columnOrder.value = orderedNames
      }

      if (variables.columnWidths) {
        const widths: Record<string, number> = {}
        value.forEach((col) => {
          if (typeof col.width === 'number') {
            widths[col.name] = col.width
          }
        })
        if (!widthsEqual(variables.columnWidths.value, widths)) {
          variables.columnWidths.value = widths
        }
      }

      if (!arraysEqual(variables.visibleColumns.value ?? [], orderedVisible)) {
        variables.visibleColumns.value = orderedVisible
      }
    },
  })

  const mergeColumns = (
    stored: PersistedColumn[],
    initial: PersistedColumn[],
  ): PersistedColumn[] => {
    const storedMap = new Map(stored.map((item) => [item.name, item]))
    const initialMap = new Map(initial.map((item) => [item.name, item]))
    const result: PersistedColumn[] = []

    stored.forEach((item) => {
      const initialItem = initialMap.get(item.name)
      if (initialItem) {
        result.push({ ...initialItem, ...item })
      }
    })

    initial.forEach((item) => {
      if (!storedMap.has(item.name)) {
        result.push(item)
      }
    })

    return result
  }

  usePersistenceItem<PersistedColumn>(namespace, PersistenceKeys.COLUMNS, persistedColumns, {
    merge: mergeColumns,
  })
}
